import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from '../atoms/Button';
import Avatar from '../atoms/Avatar';
import { useAuth } from '../../context/AuthContext';

/**
 * Navigation bar component
 * Displays the main navigation with logo and links
 */

const Navbar = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();

  // Navigation items configuration
  const navItems = [
    { label: 'Components', path: '/components' },
    { label: 'Categories', path: '/categories' },
    { label: 'About', path: '/about' },
  ];
  if (isAuthenticated) {
    navItems.push({ label: 'Library', path: '/library' });
  }

  return (
    <nav className="bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and brand */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img
                src="/logo.svg"
                alt="ModStack"
                className="h-16 w-auto"
              />
            </Link>
          </div>

          {/* Navigation Links */}
          <div className="hidden md:flex md:items-center md:space-x-6 md:justify-start flex-1 px-16">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 text-sm font-medium ${
                  location.pathname === item.path
                    ? 'text-purple-600'
                    : 'text-gray-500 hover:text-gray-900'
                } transition-colors`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            {/* Mobile menu toggle */}
            <button className="md:hidden p-2 rounded hover:bg-gray-50" onClick={() => setMobileOpen(v => !v)} aria-label="Toggle menu">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
            {!isAuthenticated ? (
              <>
                <Link to="/register">
                  <Button
                    variant="primary"
                    size="md"
                  >
                    Get Started
                  </Button>
                </Link>
                <Link to="/login">
                  <Button
                    variant="secondary"
                    size="md"
                  >
                    Sign in
                  </Button>
                </Link>
              </>
            ) : (
              <div className="relative">
                <details className="group">
                  <summary className="list-none flex items-center gap-2 cursor-pointer select-none">
                    <Avatar src={user?.profileImage} name={`${user?.firstName || ''} ${user?.lastName || ''}`} email={user?.email} />
                    <svg className="h-4 w-4 text-gray-500 group-open:rotate-180 transition-transform" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 12a1 1 0 01-.707-.293l-3-3a1 1 0 111.414-1.414L10 9.586l2.293-2.293a1 1 0 111.414 1.414l-3 3A1 1 0 0110 12z" clipRule="evenodd"/></svg>
                  </summary>
                  <div className="absolute right-0 mt-2 w-44 rounded-md border bg-white shadow-lg p-1 z-50">
                    <Link to="/profile" className="block px-3 py-2 text-sm hover:bg-gray-50 rounded">Profile</Link>
                    <button
                      onClick={logout}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded">
                      Logout
                    </button>
                  </div>
                </details>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-100 px-4 py-3 space-y-2">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path} className="block px-2 py-2 rounded hover:bg-gray-50 text-gray-700" onClick={() => setMobileOpen(false)}>
              {item.label}
            </Link>
          ))}
          {!isAuthenticated ? (
            <div className="flex gap-2">
              <Link to="/login" className="flex-1" onClick={() => setMobileOpen(false)}>
                <Button variant="secondary" size="md" className="w-full">Sign in</Button>
              </Link>
              <Link to="/register" className="flex-1" onClick={() => setMobileOpen(false)}>
                <Button variant="primary" size="md" className="w-full">Get Started</Button>
              </Link>
            </div>
          ) : (
            <button className="w-full text-left px-2 py-2 rounded hover:bg-gray-50" onClick={logout}>
              Logout
            </button>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
